import React, { useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";

import { withState } from "../../../services/state";

import {
  getMembers as apiGetMembers,
  getAvailabilityRequest as apiGetAvailabilityRequest,
  updateAvailabilityRequest as apiUpdateAvailabilityRequest,
  getTeams as apiGetTeams,
  getEventLocations as apiGetEventLocations,
  getEventTypes as apiGetEventTypes,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import Section from "../../components/section/Section";
import DialogModal from "../../components/dialogModal/DialogModal";
import Button from "../../components/button/Button";

import AddAvailabilityRequestForm from "../../forms/addAvailabilityRequestForm/AddAvailabilityRequestForm";

const FormActions = styled("div")({
  textAlign: "right",
});

const getPlayerType = (currentClub) => {
  if (currentClub.isTreasurer) return "treasurer";
  if (currentClub.isCaptain) return "captain";
  return "player";
};

const event_action = {
  captain:
    "Here you can enter details of your upcoming fixtures and pick your teams. If you hit the Slate logo to go back to the events list, the details will be saved and you or the Club Treasurer can still edit them. Please note that once you CONFIRM an event, the details cannot be changed. Payments from your players will only be collected once an event is confirmed.",
  treasurer:
    "Here you can enter details of your upcoming fixtures and pick your teams. If you hit the Slate logo to go back to the events list, the details will be saved and you or the Team Manager can still edit them. Please note that once you CONFIRM an event, the details cannot be changed. Payments from your players will only be collected once an event is confirmed.",
};

function EditAvailabilityRequestPage({ classes, dispatch, context }) {
  const { availabilityRequestId } = useParams();

  const [members, setMembers] = React.useState([]);
  const [availabilityRequest, setAvailabilityRequest] = React.useState(true);
  const [
    initialAvailabilityRequestLoading,
    setInitialAvailabilityRequestLoading,
  ] = React.useState(true);
  const [gettingTeams, setGettingTeams] = React.useState(true);
  const [teams, setTeams] = React.useState([]);
  const [gettingEventLocations, setGettingEventLocations] =
    React.useState(true);
  const [eventLocations, setEventLocations] = React.useState([]);
  const [gettingEventTypes, setGettingEventTypes] = React.useState(true);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [updatingEvent, setUpdatingEvent] = React.useState(false);
  const [showEditEventInfo, setShowEditEventInfo] = React.useState(false);

  const getMembers = useCallback(async () => {
    try {
      if (!context.currentClub.clubId) {
        return false;
      }

      const data = await apiGetMembers(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setMembers(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [context.currentClub.clubId, context.currentPlayer.id]);

  const getAvailabilityRequest = useCallback(async () => {
    try {
      const data = await apiGetAvailabilityRequest(availabilityRequestId);
      if (!availabilityRequest.id) {
        setInitialAvailabilityRequestLoading(false);
      }
      if (data && !data.reason) {
        setAvailabilityRequest({
          ...data,
          teamId: data && data.team && data.team.id,
          eventTypeId: data && data.type && data.type.id,
        });
      }
    } catch (e) {
      console.log(e);
    }
  }, [availabilityRequestId, availabilityRequest.id]);

  const updateAvailabilityRequest = async () => {
    try {
      setUpdatingEvent(true);
      const data = await apiUpdateAvailabilityRequest(
        availabilityRequestId,
        availabilityRequest
      );
      if (data && !data.reason) {
        await getAvailabilityRequest();
      }
      window.history.back();
      setUpdatingEvent(false);
    } catch (e) {
      console.log(e);
      setUpdatingEvent(false);
    }
  };

  const getTeams = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingTeams(true);
      const data = await apiGetTeams(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        let teams = data;
        if (context.currentPlayer.isCaptain) {
          teams = data.filter((team) => team.isTeamCaptain);
        }
        setTeams(teams);
      }
      setGettingTeams(false);
    } catch (e) {
      console.log(e);
      setGettingTeams(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const getEventTypes = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingEventTypes(true);
      const data = await apiGetEventTypes(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setEventTypes(data);
      }
      setGettingEventTypes(false);
    } catch (e) {
      console.log(e);
      setGettingEventTypes(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const getEventLocations = useCallback(async () => {
    try {
      setGettingEventLocations(true);
      const data = await apiGetEventLocations(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setEventLocations(data);
      }
      setGettingEventLocations(false);
    } catch (e) {
      console.log(e);
      setGettingEventLocations(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  useEffect(() => {
    getMembers();
    getAvailabilityRequest();
    getTeams();
    getEventLocations();
    getEventTypes();
  }, [
    getMembers,
    getAvailabilityRequest,
    getTeams,
    getEventLocations,
    getEventTypes,
  ]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title="Edit availability request"
      showLoading={
        gettingTeams ||
        gettingEventLocations ||
        gettingEventTypes ||
        initialAvailabilityRequestLoading
      }
      onInfoClick={
        event_action[getPlayerType(context.currentClub)]
          ? () => setShowEditEventInfo(true)
          : null
      }
    >
      <Section>
        <AddAvailabilityRequestForm
          payload={availabilityRequest}
          storePayload={setAvailabilityRequest}
          teams={teams}
          eventTypes={eventTypes}
          eventLocations={eventLocations}
          setEventLocations={setEventLocations}
          members={members}
          editing
        />
        <FormActions>
          <Button
            title="Save"
            onClick={() => {
              updateAvailabilityRequest();
            }}
            submitting={updatingEvent}
          />
        </FormActions>
      </Section>

      {showEditEventInfo && (
        <DialogModal
          title="Your events"
          onClose={() => setShowEditEventInfo(null)}
          cancelText="Close"
        >
          <div>{event_action[getPlayerType(context.currentClub)]}</div>
        </DialogModal>
      )}
    </PageWrapper>
  );
}

export default withState(EditAvailabilityRequestPage);
